import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { API_ENDPOINTS } from "../config/constants";
import ImageCropper from "./ImageCropper";

const FileUpload = ({ vehicleId, onUploadComplete, onUploadError }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const handleUpload = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append("file", file);
      formData.append("vehicleId", vehicleId);

      // Upload to server
      const response = await fetch(API_ENDPOINTS.UPLOAD_FILE, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      const vehicle = await response.json();
      onUploadComplete?.(vehicle);
    } catch (error) {
      console.error("Upload error:", error);
      onUploadError?.(error.message || "Failed to upload file");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setSelectedFile(null);
      setShowCropper(false);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const file = acceptedFiles[0];

      // If it's an image, show the cropper
      if (file.type.startsWith("image/")) {
        setSelectedFile(file);
        setShowCropper(true);
      } else {
        // If it's not an image (e.g., video), upload directly
        handleUpload(file);
      }
    },
    [vehicleId, onUploadComplete, onUploadError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".webp",
        ".bmp",
        ".tiff",
        ".svg",
        ".heic",
        ".heif",
      ],
      "video/*": [".mp4", ".webm", ".mov", ".avi"],
    },
    maxSize: 256 * 1024 * 1024, // 256MB
    multiple: false,
  });

  const handleCropComplete = (croppedFile) => {
    handleUpload(croppedFile);
  };

  const handleCropCancel = () => {
    setSelectedFile(null);
    setShowCropper(false);
  };

  return (
    <>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900"
            : "border-gray-300 hover:border-blue-400 dark:border-gray-700 dark:hover:border-blue-400"
        }`}
      >
        <input {...getInputProps()} />
        {isUploading ? (
          <div className="space-y-2">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Uploading...
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 dark:bg-blue-400"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        ) : isDragActive ? (
          <p className="text-blue-500 dark:text-blue-400">
            Drop the file here...
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-gray-600 dark:text-gray-400">
              Drag and drop an image or video here, or click to select
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Supported formats: JPG, PNG, GIF, WEBP, BMP, TIFF, SVG, HEIC, MP4,
              WEBM, MOV, AVI
              <br />
              Max size: 32MB for images, 256MB for videos
            </p>
          </div>
        )}
      </div>

      {showCropper && selectedFile && (
        <ImageCropper
          imageFile={selectedFile}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
};

export default FileUpload;
